/**
 * Zotula's field rules, word for word the same as the API's (backend/internal/platform/fields).
 * Forms check with these before sending so a mistake is shown at once under its field; the
 * API checks again and its field errors are still shown when it disagrees.
 * Every case in api/field-rules.json is run against these rules (fields.test.ts).
 * New passwords are checked by ./password.ts, kept apart so only forms that set a password
 * load the list of common passwords.
 */

export type FieldCheck = { ok: true; value: string } | { ok: false; message: string };

export const MESSAGES = {
  personNameEmpty: "Enter the full name.",
  personNameLetters: "Use at least 2 letters.",
  personNameChars: "Use letters, spaces, apostrophes, hyphens and full stops only.",
  companyNameEmpty: "Enter the company's name.",
  companyNameChars: "Use letters, digits, spaces and the marks & . , ' - ( ) / + only.",
  teamNameEmpty: "Enter the team's name.",
  jobRoleNameEmpty: "Enter the role's name.",
  statusNameEmpty: "Enter the status's name.",
  labelChars: "Use letters, digits, spaces and the marks - & / ( ) . ' + # only.",
  oneLetter: "Use at least one letter.",
  twoCharacters: "Use at least 2 characters.",
  employeeIdEmpty: "Enter an employee ID, such as EMP-0007.",
  employeeIdChars: "Use letters, digits, hyphens, underscores, slashes and full stops only, with no spaces.",
  employeeIdAlnum: "Use at least one letter or digit.",
  email: "Enter an email address, like name@company.com.",
  phoneEmpty: "Enter a mobile number, like +44 7700 900123.",
  phone: "Start with + and the country code, then the number: 8 to 15 digits, like +44 7700 900123.",
  connectCode: "Enter the 8-character code shown on the computer, like FGH7-KM3P.",
} as const;

export const LIMITS = { personName: 120, companyName: 120, label: 60, teamDescription: 500, employeeId: 32, email: 254, phone: 32 } as const;

const atMost = (n: number) => `Use at most ${n} characters.`;
const valid = (value: string): FieldCheck => ({ ok: true, value });
const invalid = (message: string): FieldCheck => ({ ok: false, message });

const PERSON_NAME_CHARS = /^[\p{L}\p{M} '’.-]+$/u;
const COMPANY_CHARS = /^[\p{L}\p{M}\p{Nd} &.,'’\-()/+]+$/u;
const LABEL_CHARS = /^[\p{L}\p{M}\p{Nd} \-&/().'’+#]+$/u;
const LETTER = /\p{L}/gu;
const EMPLOYEE_ID_CHARS = /^[A-Za-z0-9_./-]+$/;
const EMAIL_LOCAL = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const EMAIL_DOMAIN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** Characters as people count them (code points), not UTF-16 units. */
export const length = (s: string): number => [...s].length;

/** NUL removed, composed (NFC), trimmed, every run of white space made one space. */
export function cleanText(raw: string): string {
  return raw.replaceAll("\0", "").normalize("NFC").trim().split(/\s+/u).filter(Boolean).join(" ");
}

const letters = (s: string): number => s.match(LETTER)?.length ?? 0;

// Names typed all in small letters ("asha rao", "acme studio") get capital first letters, as the server stores them.
// Only a word written wholly in small letters changes: one with a capital in it ("iPhone", "McDonald") is kept.

/** Stay small after the first word of a person's name ("Ana de Souza", "Ruud van Dijk"). */
const PERSON_PARTICLES = new Set(["al", "bin", "binti", "da", "das", "de", "del", "della", "der", "di", "dos", "du", "la", "le", "van", "von"]);
/** Stay small after the first word of an organisation's name ("Bread and Butter"). */
const COMPANY_SMALL_WORDS = new Set(["a", "an", "and", "at", "de", "for", "in", "of", "on", "the", "to"]);

/** A word written all in small letters with its first letter capitalised; any other word as it is. */
function capitalFirst(word: string): string {
  if (!word || word.toLowerCase() !== word) return word;
  const [first = "", ...rest] = [...word];
  return /\p{Ll}/u.test(first) ? first.toUpperCase() + rest.join("") : word;
}

/** Each word written in small letters capitalised, except small words after the first. */
export function capitalWords(s: string, small: ReadonlySet<string> = new Set()): string {
  return s.split(" ").map((w, i) => (i > 0 && small.has(w) ? w : capitalFirst(w))).join(" ");
}

/** The first word capitalised when written in small letters: "customer support" becomes "Customer support". */
export function capitalSentence(s: string): string {
  const space = s.indexOf(" ");
  return space < 0 ? capitalFirst(s) : capitalFirst(s.slice(0, space)) + s.slice(space);
}

const capitalised = (check: FieldCheck, change: (v: string) => string): FieldCheck =>
  check.ok ? valid(change(check.value)) : check;

/** A person's full name: letters in any language, spaces, ' ’ - and full stops; 2 to 120 characters with at least 2 letters. */
export function personName(raw: string): FieldCheck {
  const v = cleanText(raw);
  if (!v) return invalid(MESSAGES.personNameEmpty);
  if (length(v) > LIMITS.personName) return invalid(atMost(LIMITS.personName));
  if (!PERSON_NAME_CHARS.test(v)) return invalid(MESSAGES.personNameChars);
  if (letters(v) < 2) return invalid(MESSAGES.personNameLetters);
  return valid(capitalWords(v, PERSON_PARTICLES));
}

function label(raw: string, empty: string, max: number, chars: RegExp, charsMessage: string): FieldCheck {
  const v = cleanText(raw);
  if (!v) return invalid(empty);
  if (length(v) > max) return invalid(atMost(max));
  if (!chars.test(v)) return invalid(charsMessage);
  if (letters(v) === 0) return invalid(MESSAGES.oneLetter);
  if (length(v) < 2) return invalid(MESSAGES.twoCharacters);
  return valid(v);
}

/** An organisation's name: 2 to 120 characters with at least one letter; letters, digits, spaces and & . , ' - ( ) / +. */
export const companyName = (raw: string) =>
  capitalised(label(raw, MESSAGES.companyNameEmpty, LIMITS.companyName, COMPANY_CHARS, MESSAGES.companyNameChars), (v) => capitalWords(v, COMPANY_SMALL_WORDS));

/** A team's name: 2 to 60 characters with at least one letter; letters, digits, spaces and - & / ( ) . ' + #. */
export const teamName = (raw: string) => capitalised(label(raw, MESSAGES.teamNameEmpty, LIMITS.label, LABEL_CHARS, MESSAGES.labelChars), capitalSentence);

/** A team's description: any text, cleaned like a name, of at most 500 characters. Empty is fine. */
export function teamDescription(raw: string): FieldCheck {
  const v = cleanText(raw);
  if (length(v) > LIMITS.teamDescription) return invalid(atMost(LIMITS.teamDescription));
  return valid(v);
}

/** A job role's name, by the team name rule. */
export const jobRoleName = (raw: string) => capitalised(label(raw, MESSAGES.jobRoleNameEmpty, LIMITS.label, LABEL_CHARS, MESSAGES.labelChars), capitalSentence);
/** An attendance status's name ("Meeting"): the same rule as a team's. */
export const statusName = (raw: string) => capitalised(label(raw, MESSAGES.statusNameEmpty, LIMITS.label, LABEL_CHARS, MESSAGES.labelChars), capitalSentence);

/** An employee ID: 1 to 32 ASCII letters, digits, - _ / and full stops, with a letter or digit. Letter case is kept. */
export function employeeId(raw: string): FieldCheck {
  const v = raw.replaceAll("\0", "").trim();
  if (!v) return invalid(MESSAGES.employeeIdEmpty);
  if (length(v) > LIMITS.employeeId) return invalid(atMost(LIMITS.employeeId));
  if (!EMPLOYEE_ID_CHARS.test(v)) return invalid(MESSAGES.employeeIdChars);
  if (!/[A-Za-z0-9]/.test(v)) return invalid(MESSAGES.employeeIdAlnum);
  return valid(v);
}

/** An email address as stored (trimmed, lower-case): one @, a name part of 1 to 64 characters, a domain with a dot and a top-level part of 2 or more letters. */
export function email(raw: string): FieldCheck {
  const v = raw.trim().toLowerCase();
  if (!v || v.length > LIMITS.email) return invalid(MESSAGES.email);
  const at = v.indexOf("@");
  if (at < 0 || v.indexOf("@", at + 1) >= 0) return invalid(MESSAGES.email);
  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (local.length > 64 || domain.length > 253 || !EMAIL_LOCAL.test(local) || !EMAIL_DOMAIN.test(domain)) return invalid(MESSAGES.email);
  return valid(v);
}

/** A mobile number in international form: +, then 8 to 15 digits, spaces and dashes allowed; stored as + and the digits. */
export function phone(raw: string): FieldCheck {
  const v = raw.replaceAll("\0", "").trim();
  if (!v) return invalid(MESSAGES.phoneEmpty);
  if (length(v) > LIMITS.phone) return invalid(atMost(LIMITS.phone));
  if (!/^\+[0-9 -]*$/.test(v)) return invalid(MESSAGES.phone);
  const digits = v.replace(/[ -]/g, "").slice(1);
  if (digits.length < 8 || digits.length > 15) return invalid(MESSAGES.phone);
  return valid(`+${digits}`);
}

/** The letters and digits a connect code is made of: no vowels, none of I/1/L, O/0, U. */
const CONNECT_CODE_ALPHABET = "BCDFGHJKMNPQRSTVWXZ23456789";
const CONNECT_CODE_LENGTH = 8;

/**
 * The code the Zotula icon shows on a computer waiting to be connected, as the API hashes it:
 * upper case, hyphens and spaces dropped, 8 characters of the alphabet. Same rule as the server.
 */
export function connectCode(raw: string): FieldCheck {
  let out = "";
  for (const ch of raw.trim().toUpperCase()) {
    if (ch === "-" || ch === " ") continue;
    if (!CONNECT_CODE_ALPHABET.includes(ch)) return invalid(MESSAGES.connectCode);
    out += ch;
  }
  if (out.length !== CONNECT_CODE_LENGTH) return invalid(MESSAGES.connectCode);
  return valid(out);
}

/** A connect code as it is shown: XXXX-XXXX. */
export function formatConnectCode(code: string): string {
  return code.length === CONNECT_CODE_LENGTH ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

/** Moves focus to the field marked data-field=name (WCAG 3.3.1: take the person to what needs them). */
export function focusField(name: string | undefined): void {
  if (name) document.querySelector<HTMLElement>(`[data-field="${name}"]`)?.focus();
}

/**
 * Runs each check and returns the cleaned values and the problems by field, in order, so a
 * form can show every message at once and focus the first.
 */
export function checkAll<K extends string>(checks: Record<K, FieldCheck | null>): { values: Partial<Record<K, string>>; problems: Partial<Record<K, string>>; first?: K } {
  const values: Partial<Record<K, string>> = {};
  const problems: Partial<Record<K, string>> = {};
  let first: K | undefined;
  for (const key of Object.keys(checks) as K[]) {
    const result = checks[key];
    if (!result) continue;
    if (result.ok) values[key] = result.value;
    else {
      problems[key] = result.message;
      first ??= key;
    }
  }
  return { values, problems, first };
}
